import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useNewApplicationContext } from "../providers/NewApplicationProvider";
import FullscreenLoader from "../components/FullscreenLoader";
import { useConfigContext } from "../providers/ConfigProvider";
import Button from "../components/Button";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import ModalPage from "../components/ModalPage";
import useKine from "../hooks/useKine";
import { Avatar } from "../components/Kine";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Application = () => {
    const { t } = useTranslation();
    const { draftId: configDraftId, setDraftId } = useConfigContext();
    const { consentKine, setConsentKine, assistantAvailable } = useKine();
    const { sandbox, debugBypass, setDebugBypass, refreshApplicationConfig, application, schema, setApplicationData, getDraftFromDataBase, next, applicationId, contextDone } = useNewApplicationContext();
    // Lekeplassen åpner aldri en kladd (se NewApplicationProvider).
    const draftId = sandbox ? null : configDraftId;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const loadedDraftRef = useRef<number | null>(null);

    const navigate = useNavigate();
    const params = useParams();

    const isDev = import.meta.env.DEV;

    const handleRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await refreshApplicationConfig();
        } finally {
            // Liten timeout slik at spin-animasjonen rekker å gå minst én runde
            setTimeout(() => setIsRefreshing(false), 400);
        }
    };

    useEffect(() => {
        if (!application) {
            setApplicationData(params.formType ?? '')
        }
    });
    
    useEffect(() => {
        if (!application || !schema || !contextDone) return;
        if (!draftId || applicationId !== null) return;
        if (loadedDraftRef.current === draftId) return;

        loadedDraftRef.current = draftId;
        getDraftFromDataBase()
            .then((url) => {
                if (url) navigate(url);
                else if (!params.section) next();
            })
            .catch((error) => {
                console.error('Could not load draft:', error);
                setDraftId(null);
            });
    }, [application, schema, contextDone, draftId, applicationId]);

    if (application === null || !contextDone || (draftId && applicationId === null)) {
        return <FullscreenLoader />
    }

    if(consentKine === undefined && assistantAvailable){
        return (
                <AnimatePresence mode="wait">
                    <ModalPage modalType={'middleSection'} showCheck={false}>
                        <h1>Ønsker du hjelp av vår KI-assistent?</h1>
                        <div className="py-2">
                            <Avatar loading={false} typing={false} maxWidth={400} />
                        </div>
                        <div className="text-center my-3">
                            <div className="row justify-content-center g-3">
                                <div className="col-6">
                                    <Button title="Nei takk!" classes="w-100" type={"primary"} onClick={() => setConsentKine(false)} />
                                </div>
                                <div className="col-6">
                                    <Button title="Ja takk, aktiver KI" classes="w-100" type="rainbow" leftIcon="sparkles" onClick={() => setConsentKine(true)} />
                                </div>
                            </div>
                            
                        </div>
                        <div className="text-center">
                            <p>Ved aktivering deles innhold fra din søknad med OpenAI, men <b>kun når du ber om en vurdering av KI</b>. Se mer i vår <a href="#">personvernerklæring</a>. Kunstig Intelligens er et godt hjelpemiddel, men kan gjøre feil. Ta trådene og tilbakemeldingene du får som innspill.</p>
                            {sandbox && (
                                <p className="fw-semibold text-warning-emphasis">
                                    <FontAwesomeIcon icon={['fal', 'flask']} className="me-2" />
                                    {t('playground_iver_note')}
                                </p>
                            )}
                        </div>
                    </ModalPage>
                </AnimatePresence>
            )
    }

    if (params.section) {
        return (
            <>
                <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                    {isDev && (
                        <div className="d-flex align-items-center">
                            <div
                                className="form-check form-switch mb-0 me-3"
                                title="Hopp over validering — la deg klikke Neste og Send selv om felter mangler (DEV)"
                            >
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="lyftiDebugBypass"
                                    checked={debugBypass}
                                    onChange={(e) => setDebugBypass(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <label
                                    className="form-check-label small text-muted"
                                    htmlFor="lyftiDebugBypass"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                    Debug
                                </label>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-link p-0 me-3 text-decoration-none"
                                onClick={handleRefresh}
                                title="Last skjemaconfig på nytt (DEV)"
                                disabled={isRefreshing}
                                style={{ lineHeight: 1 }}
                            >
                                <FontAwesomeIcon
                                    icon={['fal', 'arrows-rotate']}
                                    spin={isRefreshing}
                                    className="text-primary"
                                    size="lg"
                                />
                            </button>
                        </div>
                    )}
                </div>
                <Outlet />
            </>);
    }

    if (!params.section && !draftId) {
        next()
    }

    return (
        <div>
            <Button
                title={t('application_start')}
                type="primary"
                rightIcon="arrow-right"
                onClick={() => next()}
            />
        </div>
    );
}

export default Application;